'use client'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
    Favorite as SupportIcon,
    Key as KeyIcon,
    CheckCircle as CheckCircleIcon,
    LockOpen as UnlockIcon,
    ContentCopy as CopyIcon,
    LinkOff as UnlinkIcon
} from '@mui/icons-material'
import { useLanguage } from '@/contexts/LanguageContext'
import { usePro } from '@/contexts/ProContext'
import { useAuth } from '@/contexts/AuthContext'
import { getBridge, isShell } from '@/utils/bridge'

const BuyContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 100%;
    color: white;
    padding: 20px 32px 32px 32px;
    box-sizing: border-box;
    overflow-y: auto;
`;

const IntroSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 950px;
    margin-bottom: 18px;
    gap: 10px;
`;

const MainTitle = styled.h1`
    font-size: 1.8rem;
    font-weight: 900;
    margin: 0 0 6px 0;
    font-family: 'Chakra Petch', sans-serif;
    text-transform: uppercase;
    letter-spacing: 1px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 90px;

    span {
        background: linear-gradient(135deg, #f59e0b 0%, #fcd34d 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        text-shadow: 0 0 30px rgba(245, 158, 11, 0.4);
    }

    svg {
        color: #f59e0b;
        font-size: 1.8rem;
        filter: drop-shadow(0 0 15px rgba(245, 158, 11, 0.6));
    }

`;

const Subtitle = styled.p`
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin: 0;
    max-width: 950px;
`;

const LayoutGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    width: 100%;
    max-width: 1100px;
    align-items: stretch;
`;

const Card = styled.div`
    background: rgba(15, 20, 30, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.04);
    border-radius: 16px;
    padding: 28px;
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(12px);
    display: flex;
    flex-direction: column;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: radial-gradient(circle at top right, rgba(245, 158, 11, 0.05), transparent 60%);
        pointer-events: none;
    }
`;

const CardTitle = styled.h2`
    font-size: 1rem;
    font-family: 'Chakra Petch', sans-serif;
    color: white;
    margin: 0 0 20px 0;
    display: flex;
    align-items: center;
    gap: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    svg {
        color: #f59e0b;
        font-size: 18px;
    }
`;

const KeyInputRow = styled.div`
    display: flex;
    gap: 8px;
    width: 100%;
`;

const BigKeyInput = styled.input`
    flex: 1;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    padding: 13px 16px;
    font-size: 14px;
    font-family: 'Consolas', monospace;
    color: #fff;
    outline: none;
    transition: border-color 0.2s, background 0.2s;
    letter-spacing: 1.5px;
    min-width: 0;

    &:focus {
        border-color: rgba(245, 158, 11, 0.5);
        background: rgba(245, 158, 11, 0.04);
    }

    &::placeholder {
        color: rgba(255, 255, 255, 0.15);
        letter-spacing: 1px;
    }
`;

const ActivateBtn = styled.button`
    padding: 13px 20px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    font-family: 'Chakra Petch', sans-serif;
    letter-spacing: 1px;
    cursor: pointer;
    text-transform: uppercase;
    border: 1px solid rgba(245, 158, 11, 0.5);
    background: rgba(245, 158, 11, 0.12);
    color: #fcd34d;
    transition: all 0.2s;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background: rgba(245, 158, 11, 0.25);
        border-color: #f59e0b;
        color: #fff;
        box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

const ErrorMsg = styled.div`
    font-size: 0.8rem;
    color: #f87171;
    background: rgba(239, 68, 68, 0.08);
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: 8px;
    padding: 10px 14px;
    margin-top: 10px;
`;

const SuccessMsg = styled.div`
    font-size: 0.82rem;
    color: #34d399;
    background: rgba(52, 211, 153, 0.08);
    border: 1px solid rgba(52, 211, 153, 0.2);
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 10px;

    svg {
        font-size: 20px;
        flex-shrink: 0;
    }
`;

const KeyHint = styled.p`
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.25);
    margin: 10px 0 0 0;
    line-height: 1.5;
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background: rgba(255, 255, 255, 0.06);
    margin: 22px 0;
`;

const WhereTitle = styled.h3`
    font-size: 0.8rem;
    font-family: 'Chakra Petch', sans-serif;
    color: rgba(255, 255, 255, 0.45);
    margin: 0 0 6px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const WhereDesc = styled.p`
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.35);
    margin: 0 0 14px 0;
    line-height: 1.5;
`;

const WhereLinkRow = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

const WhereLink = styled.a`
    font-size: 0.78rem;
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 600;
    color: rgba(245, 158, 11, 0.75);
    text-decoration: none;
    padding: 6px 14px;
    border: 1px solid rgba(245, 158, 11, 0.2);
    border-radius: 6px;
    transition: all 0.2s;
    letter-spacing: 0.5px;
    text-transform: uppercase;

    &:hover {
        color: #f59e0b;
        border-color: rgba(245, 158, 11, 0.5);
        background: rgba(245, 158, 11, 0.08);
    }
`;

const ActionCard = styled(Card)`
    border-color: rgba(245, 158, 11, 0.2);
    align-items: center;
    text-align: center;
    background: linear-gradient(180deg, rgba(15, 20, 30, 0.8) 0%, rgba(245, 158, 11, 0.08) 100%);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
    justify-content: center;

    &::before {
        background: radial-gradient(circle at center, rgba(245, 158, 11, 0.12), transparent 70%);
    }

    .action-icon {
        font-size: 36px;
        color: #f59e0b;
        margin-bottom: 16px;
        filter: drop-shadow(0 0 20px rgba(245, 158, 11, 0.5));
    }
`;

const CardText = styled.p`
    font-size: 0.82rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
    margin: 0 0 20px 0;

    strong {
        color: white;
        font-weight: 600;
        text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    max-width: 280px;
    margin-top: 16px;
`;

const SupporterButton = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 12px 16px;
    border-radius: 10px;
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 700;
    font-size: 0.82rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: white;
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-sizing: border-box;

    background: ${props => props.$variant === 'patreon' ? 'linear-gradient(135deg, #f96854 0%, #d84531 100%)' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'};
    box-shadow: 0 4px 15px ${props => props.$variant === 'patreon' ? 'rgba(249, 104, 84, 0.2)' : 'rgba(99, 102, 241, 0.2)'};

    &:hover {
        transform: translateY(-2px) scale(1.02);
        box-shadow: 0 8px 20px ${props => props.$variant === 'patreon' ? 'rgba(249, 104, 84, 0.4)' : 'rgba(99, 102, 241, 0.4)'};
        filter: brightness(1.1);
    }

    &:active {
        transform: translateY(0) scale(1);
    }

    svg {
        font-size: 18px;
    }
`;

const PatreonButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    border: none;
    border-radius: 10px;
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 700;
    font-size: 0.82rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #2a0f0b;
    background: linear-gradient(135deg, #f96854 0%, #d84531 100%);
    box-shadow: 0 4px 15px rgba(249, 104, 84, 0.2);
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(249, 104, 84, 0.4);
        filter: brightness(1.1);
    }

    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const LinkedRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    font-size: 0.82rem;
    color: rgba(255, 255, 255, 0.7);
`;

const GhostButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.5);
    border-radius: 8px;
    padding: 7px 12px;
    font-size: 0.72rem;
    font-family: 'Chakra Petch', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) { color: #fff; border-color: rgba(255, 255, 255, 0.3); }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const KeyBox = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 14px;
    padding: 10px 14px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    font-family: 'Consolas', monospace;
    font-size: 0.9rem;
    letter-spacing: 1px;
    color: #a78bfa;
    word-break: break-all;
`;

const BlogView = () => {
    const { t } = useLanguage()
    const { isPro, activateLicense, isValidating, activateError, refreshAccess } = usePro()
    const { isAuthenticated } = useAuth()
    const [key, setKey] = useState('')
    const [success, setSuccess] = useState(false)

    // --- Patreon ---
    const [patreon, setPatreon] = useState(null)
    const [linking, setLinking] = useState(false)
    const [linkError, setLinkError] = useState(null)
    const [linkUrl, setLinkUrl] = useState(null)
    const [copiado, setCopiado] = useState(false)
    const pollRef = useRef(null)

    const cargarEstado = useCallback(async () => {
        try {
            const res = await fetch('/api/patreon/status', { credentials: 'same-origin' })
            if (!res.ok) return null
            const data = await res.json()
            setPatreon(data)
            return data
        } catch {
            return null
        }
    }, [])

    useEffect(() => {
        cargarEstado()
        return () => { if (pollRef.current) clearInterval(pollRef.current) }
    }, [cargarEstado])

    // El login de Patreon se abre en el navegador del sistema, que no puede
    // avisarnos nada de vuelta: la app se entera pooleando hasta ver el vínculo.
    const esperarVinculo = useCallback(() => {
        if (pollRef.current) clearInterval(pollRef.current)
        const hasta = Date.now() + 5 * 60 * 1000
        pollRef.current = setInterval(async () => {
            const data = await cargarEstado()
            if (data?.linked || Date.now() > hasta) {
                clearInterval(pollRef.current)
                pollRef.current = null
                setLinking(false)
                setLinkUrl(null)
                if (data?.linked) await refreshAccess()
            }
        }, 2000)
    }, [cargarEstado, refreshAccess])

    const vincularPatreon = async () => {
        if (linking) return
        setLinking(true)
        setLinkError(null)
        try {
            const res = await fetch('/api/patreon/pair', { method: 'POST', credentials: 'same-origin' })
            const data = await res.json().catch(() => ({}))
            if (!res.ok || !data.url) {
                setLinkError(data?.error ?? 'No se pudo iniciar la vinculación.')
                setLinking(false)
                return
            }

            // Dentro del shell hay que salir al navegador real: ahí está la
            // sesión de Patreon del usuario y su gestor de contraseñas, y el
            // login no anda bien dentro de una webview embebida. openExternal
            // puede no existir en shells viejos ya instalados.
            const bridge = getBridge()
            if (isShell() && typeof bridge?.openExternal === 'function') {
                await bridge.openExternal(data.url)
            } else {
                window.open(data.url, '_blank', 'noopener')
            }
            setLinkUrl(data.url)
            esperarVinculo()
        } catch {
            setLinkError('No se pudo conectar con el servidor.')
            setLinking(false)
        }
    }

    const desvincularPatreon = async () => {
        setLinkError(null)
        try {
            await fetch('/api/patreon/unlink', { method: 'POST', credentials: 'same-origin' })
            await cargarEstado()
            await refreshAccess()
        } catch {
            setLinkError('No se pudo desvincular.')
        }
    }

    const copiarKey = () => {
        if (!patreon?.key?.code) return
        navigator.clipboard?.writeText(patreon.key.code)
        setCopiado(true)
        setTimeout(() => setCopiado(false), 2000)
    }

    const handleExternalLink = (e, url) => {
        // En browser puro el <a target="_blank"> de abajo ya alcanza; el bridge solo
        // hace falta dentro del shell nativo (para no navegar la webview embebida).
        if (isShell()) {
            e.preventDefault()
            getBridge()?.openExternal(url)
        }
    }

    const handleActivate = async () => {
        const ok = await activateLicense(key)
        if (ok) setSuccess(true)
    }

    return (
        <BuyContainer>
            <IntroSection>
                <MainTitle>
                    <SupportIcon /> <span dangerouslySetInnerHTML={{ __html: t('BUY_TITLE') }} />
                </MainTitle>
                <Subtitle dangerouslySetInnerHTML={{ __html: t('BUY_SUBTITLE') }} />
            </IntroSection>

            <LayoutGrid>
                <Card>
                    <CardTitle>
                        <KeyIcon /> {t('BUY_ACTIVATE_CARD_TITLE')}
                    </CardTitle>

                    {success ? (
                        <SuccessMsg>
                            <CheckCircleIcon />
                            {t('BUY_KEY_SUCCESS')}
                        </SuccessMsg>
                    ) : (
                        <>
                            {/* El campo se muestra IGUAL teniendo PRO: si a
                                alguien le sobra una clave tiene que poder
                                escribirla y que se le diga que la regale, en
                                vez de encontrarse sin dónde ponerla. */}
                            {isPro && (
                                <SuccessMsg style={{ marginBottom: 16 }}>
                                    <CheckCircleIcon />
                                    {t('BUY_KEY_ALREADY_PRO')}
                                </SuccessMsg>
                            )}
                            <KeyInputRow>
                                <BigKeyInput
                                    placeholder="XXXX-XXXX-XXXX-XXXX"
                                    value={key}
                                    onChange={e => setKey(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleActivate()}
                                    spellCheck={false}
                                />
                                <ActivateBtn
                                    onClick={handleActivate}
                                    disabled={isValidating || !key.trim()}
                                >
                                    <KeyIcon style={{ fontSize: 15 }} />
                                    {isValidating ? t('BUY_KEY_VALIDATING') : t('BUY_KEY_BTN')}
                                </ActivateBtn>
                            </KeyInputRow>

                            {activateError && <ErrorMsg>{activateError}</ErrorMsg>}

                            <KeyHint>{t('BUY_KEY_HINT')}</KeyHint>
                        </>
                    )}

                    <Divider />

                    <WhereTitle>{t('BUY_KEY_WHERE_TITLE')}</WhereTitle>
                    <WhereDesc>{t('BUY_KEY_WHERE_DESC')}</WhereDesc>
                    <WhereLinkRow>
                        <WhereLink
                            href="https://www.patreon.com/ConfigStrike"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => handleExternalLink(e, 'https://www.patreon.com/ConfigStrike')}
                        >
                            Patreon
                        </WhereLink>
                    </WhereLinkRow>
                </Card>

                {/* Vinculación con Patreon: la vía principal de acceso. Solo
                    tiene sentido con sesión (el vínculo se ata a la cuenta de
                    Steam) y si el server tiene la campaña configurada. */}
                {isAuthenticated && patreon?.configured && (
                    <Card>
                        <CardTitle>
                            <SupportIcon /> Patreon
                        </CardTitle>

                        {patreon.linked ? (
                            <>
                                <LinkedRow>
                                    <CheckCircleIcon style={{ fontSize: 18, color: '#34d399' }} />
                                    <span>
                                        Vinculado
                                        {patreon.patreonFullName ? ` como ${patreon.patreonFullName}` : ''}
                                        {patreon.lastStatus === 'former_patron' && ' — la suscripción ya no está activa'}
                                        {patreon.lastStatus === 'declined_patron' && ' — el último cobro fue rechazado'}
                                    </span>
                                    <GhostButton onClick={desvincularPatreon}>
                                        <UnlinkIcon style={{ fontSize: 14 }} /> Desvincular
                                    </GhostButton>
                                </LinkedRow>

                                {patreon.key && (
                                    <>
                                        <KeyHint style={{ marginTop: 16 }}>
                                            Tu clave personal. Sirve para activar PRO en otra cuenta y vale
                                            mientras tu suscripción siga activa.
                                        </KeyHint>
                                        <KeyBox>
                                            <span style={{ flex: 1 }}>{patreon.key.code}</span>
                                            <GhostButton onClick={copiarKey}>
                                                <CopyIcon style={{ fontSize: 14 }} />
                                                {copiado ? 'Copiada' : 'Copiar'}
                                            </GhostButton>
                                        </KeyBox>
                                        {patreon.key.usedCount >= patreon.key.maxUses && (
                                            <KeyHint>Ya fue canjeada.</KeyHint>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <CardText>
                                    Vinculá tu cuenta de Patreon una sola vez y tu acceso PRO se
                                    activa solo, en cualquier PC donde entres con tu Steam.
                                </CardText>
                                <PatreonButton onClick={vincularPatreon} disabled={linking}>
                                    {linking ? 'Esperando a Patreon…' : 'Vincular con Patreon'}
                                </PatreonButton>
                                {linking && linkUrl && (
                                    <KeyHint>
                                        Se abrió una pestaña para que entres a Patreon. Si no la ves,
                                        <GhostButton
                                            style={{ marginLeft: 8 }}
                                            onClick={() => {
                                                navigator.clipboard?.writeText(linkUrl)
                                                setCopiado(true)
                                                setTimeout(() => setCopiado(false), 2000)
                                            }}
                                        >
                                            {copiado ? 'Copiado' : 'copiar el enlace'}
                                        </GhostButton>
                                    </KeyHint>
                                )}
                                {linkError && <ErrorMsg>{linkError}</ErrorMsg>}
                            </>
                        )}
                    </Card>
                )}

            </LayoutGrid>
        </BuyContainer>
    )
}

export default BlogView
